import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Grid, LinearProgress, Link, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InboxIcon from '@mui/icons-material/Inbox';
import BlockIcon from '@mui/icons-material/Block';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import SubmissionAppService from '../../../../application/platform/ser/submission/submission-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { ISubmissionDetailResponse } from '../../../../model/ser/submission/response/submission-detail-response';
import { ISubmissionDocumentResponse } from '../../../../model/ser/submission/response/submission-document-response';
import { formatDate, formatDateTime } from '../../../../utils/date';
import { SubmissionStatusChip, SubmissionTypeChip } from '../../components/submission-chips';
import { SubmissionDocuments } from './submission-documents';
import { HistoryTimeline } from './history-timeline';
import { FileContentDialog } from './file-content-dialog';

export const CANNOT_CANCEL_HINT = 'Esta remessa não pode mais ser cancelada';

function Info({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
            <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>{children || '—'}</Typography>
        </Box>
    );
}

interface SubmissionDetailDrawerProps {
    submissionId?: number;
    // Muda após ações externas (cancelamento) para recarregar o detalhe aberto
    refreshVersion: number;
    onClose: () => void;
    onOpenBatch: (batchId: number) => void;
    onCancel: (target: { id: number; title?: string }) => void;
    onChanged: () => void;
}

export function SubmissionDetailDrawer({ submissionId, refreshVersion, onClose, onOpenBatch, onCancel, onChanged }: SubmissionDetailDrawerProps) {
    const { showError, showSuccess } = useFeedback();
    const [detail, setDetail] = useState<ISubmissionDetailResponse>();
    const [loading, setLoading] = useState(false);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [contentDocument, setContentDocument] = useState<ISubmissionDocumentResponse>();

    useEffect(() => {
        if (!submissionId) {
            setDetail(undefined);
            return;
        }
        setLoading(true);
        SubmissionAppService.getById(submissionId)
            .then(setDetail)
            .catch((error) => {
                const notFound = error?.originalError?.response?.status === 404;
                showError(notFound ? 'Remessa não encontrada' : 'Não foi possível carregar a remessa', notFound ? undefined : error);
                onClose();
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionId, refreshVersion]);

    async function downloadAll() {
        if (!detail) return;
        setDownloadingAll(true);
        try {
            (await SubmissionAppService.downloadAll(detail.id)).download();
        } catch (error: any) {
            showError('Não foi possível baixar os arquivos', error);
        } finally {
            setDownloadingAll(false);
        }
    }

    async function downloadFile(document: ISubmissionDocumentResponse) {
        try {
            (await SubmissionAppService.downloadFile(detail!.id, document.id)).download();
        } catch (error: any) {
            showError('Não foi possível baixar o arquivo', error);
        }
    }

    async function reprocess(document: ISubmissionDocumentResponse) {
        try {
            await SubmissionAppService.reprocessContent(detail!.id, document.id);
            showSuccess('Extração reenviada para processamento');
            onChanged();
        } catch (error: any) {
            showError('Não foi possível reprocessar a extração', error);
        }
    }

    const footer = (
        <React.Fragment>
            <Tooltip title={detail && !detail.canCancel ? CANNOT_CANCEL_HINT : ''}>
                <span>
                    <Button
                        color="error"
                        variant="outlined"
                        startIcon={<BlockIcon />}
                        disabled={!detail?.canCancel}
                        onClick={() => detail && onCancel({ id: detail.id, title: detail.title })}
                    >
                        Cancelar remessa
                    </Button>
                </span>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose}>Fechar</Button>
            <LoadingButton
                variant="contained"
                startIcon={<FolderZipIcon />}
                loadingPosition="start"
                loading={downloadingAll}
                disabled={!detail?.documents?.length}
                onClick={downloadAll}
            >
                Baixar todos
            </LoadingButton>
        </React.Fragment>
    );

    return (
        <React.Fragment>
            <FormDrawer
                open={submissionId !== undefined}
                icon={<InboxIcon />}
                title={`Remessa nº ${submissionId ?? ''}`}
                subtitle={detail?.title}
                onClose={onClose}
                footer={footer}
            >
                {loading && <LinearProgress />}
                {detail && (
                    <React.Fragment>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: loading ? -2 : 0 }}>
                            <SubmissionStatusChip statusId={detail.statusId} label={detail.status} />
                            <SubmissionTypeChip type={detail.type} />
                            {detail.batchId && (
                                <Chip size="small" variant="outlined" label={`Lote nº ${detail.batchId}`} onClick={() => onOpenBatch(detail.batchId!)} />
                            )}
                        </Box>

                        {detail.cancellation?.date && (
                            <Alert severity="error" icon={<BlockIcon />}>
                                <AlertTitle>Cancelada em {formatDateTime(detail.cancellation.date)}{detail.cancellation.user?.name ? ` por ${detail.cancellation.user.name}` : ''}</AlertTitle>
                                {detail.cancellation.reason}
                            </Alert>
                        )}

                        <FormDrawerGroup title="Resumo">
                            <Grid container spacing={2}>
                                <Grid item xs={12}><Info label="Título">{detail.title}</Info></Grid>
                                <Grid item xs={6} sm={4}><Info label="Enviada em">{formatDateTime(detail.sendDate)}</Info></Grid>
                                <Grid item xs={6} sm={4}><Info label="Publicação">{formatDate(detail.publishDate)}</Info></Grid>
                                <Grid item xs={6} sm={4}><Info label="Processada em">{formatDateTime(detail.processedDate)}</Info></Grid>
                                <Grid item xs={12} sm={8}><Info label="Entidade">{detail.clientName}</Info></Grid>
                                <Grid item xs={6} sm={4}>
                                    <Info label="Lote">
                                        {detail.batchId
                                            ? <Link component="button" variant="body2" onClick={() => onOpenBatch(detail.batchId!)}>nº {detail.batchId}</Link>
                                            : null}
                                    </Info>
                                </Grid>
                                <Grid item xs={12} sm={6}><Info label="Remetente">{[detail.userSender?.name, detail.userSender?.email].filter(Boolean).join(' · ')}</Info></Grid>
                                <Grid item xs={12} sm={6}><Info label="Recebida por">{detail.userReceiver?.name || 'Ainda não recebida pela diagramação'}</Info></Grid>
                            </Grid>
                        </FormDrawerGroup>

                        {(detail.instruction || detail.diagramerObservation) && (
                            <FormDrawerGroup title="Instruções e observações">
                                {detail.instruction && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detail.instruction}</Typography>}
                                {detail.diagramerObservation && (
                                    <Alert severity="info" sx={{ mt: detail.instruction ? 2 : 0 }}>
                                        <AlertTitle>Observação da diagramação</AlertTitle>
                                        <Box sx={{ whiteSpace: 'pre-wrap' }}>{detail.diagramerObservation}</Box>
                                    </Alert>
                                )}
                            </FormDrawerGroup>
                        )}

                        <FormDrawerGroup title="Veículos de publicação">
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {(detail.vehicles ?? []).map((v) => <Chip key={v.id} label={v.name} />)}
                                {!detail.vehicles?.length && <Typography variant="body2" color="text.secondary">Nenhum veículo.</Typography>}
                            </Box>
                        </FormDrawerGroup>

                        <FormDrawerGroup title={`Documentos (${detail.documents?.length ?? 0})`}>
                            <SubmissionDocuments
                                documents={detail.documents ?? []}
                                onDownload={downloadFile}
                                onViewContent={setContentDocument}
                                onReprocess={reprocess}
                            />
                        </FormDrawerGroup>

                        <FormDrawerGroup title="Histórico">
                            <HistoryTimeline history={detail.history ?? []} />
                        </FormDrawerGroup>
                    </React.Fragment>
                )}
            </FormDrawer>
            {detail && <FileContentDialog submissionId={detail.id} document={contentDocument} onClose={() => setContentDocument(undefined)} />}
        </React.Fragment>
    );
}
